import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Download } from "lucide-react";
import { format } from "date-fns";
import { AnalyticsSummaryCards } from "./AnalyticsSummaryCards";
import { AnalyticsCharts } from "./AnalyticsCharts";
import { useAnalytics } from "@/hooks/useAnalytics";
import { cn } from "@/lib/utils";

export const ConversationAnalytics = () => {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
    to: new Date(),
  });

  const { analytics, loading } = useAnalytics(dateRange);

  const handleExport = () => {
    if (!analytics) return;
    
    const csv = [
      ['Metric', 'Value'],
      ['Total Conversations', analytics.totalConversations],
      ['Average Response Time (min)', analytics.avgResponseTime],
      ['Average Resolution Time (min)', analytics.avgResolutionTime],
      ['Active Conversations', analytics.activeConversations],
      ['Resolved Conversations', analytics.resolvedConversations],
      ['Escalated Conversations', analytics.escalatedConversations],
      ['New Customers', analytics.newCustomers],
      ['Returning Customers', analytics.returningCustomers],
      ['Average Messages per Conversation', analytics.avgMessagesPerConversation],
      ['Positive Sentiment', analytics.positiveSentiment],
      ['Negative Sentiment', analytics.negativeSentiment],
      ['Neutral Sentiment', analytics.neutralSentiment],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (loading || !analytics) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Loading analytics...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-[var(--shadow-soft)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Conversation Analytics</CardTitle>
              <CardDescription>
                Insights and metrics for your conversations
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="p-3 space-y-3">
                    <div>
                      <p className="text-sm font-medium mb-2">From</p>
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => date && setDateRange({ ...dateRange, from: date })}
                        disabled={(date) => date > dateRange.to || date > new Date()}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">To</p>
                      <Calendar
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => date && setDateRange({ ...dateRange, to: date })}
                        disabled={(date) => date < dateRange.from || date > new Date()}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={handleExport} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <AnalyticsSummaryCards analytics={analytics} />
      <AnalyticsCharts analytics={analytics} />
    </div>
  );
};
